import './process-performance.css'

import { Flex, Text } from '../../../Components'
import { useCQuery } from '../../../Services'
import { cap } from '../../../State'
import { translations } from '../../../translations'
import { formatMs, STATE_COLORS, STATE_LABELS } from './utils'

const language = cap.language.state || 'IT'
const t = translations[language]

const AnomalyCard = ({ anomaly }) => {
  const isCritical = anomaly.status === 'critical'
  const borderColor = isCritical ? STATE_COLORS.critical : STATE_COLORS.warning

  return (
    <div
      className={`ncard perf-anomaly-card ${isCritical ? 'perf-anomaly-card--critical' : 'perf-anomaly-card--warning'}`}
      style={{ borderColor }}
    >
      <div className="ncard__body">
        <Flex row jb fw>
          <Text bold value={anomaly.unit} size={14} />
          <Text
            bold
            value={STATE_LABELS[anomaly.status]}
            size={12}
            style={{ color: borderColor }}
          />
        </Flex>
        <Flex row fw style={{ marginTop: 6, gap: 16 }}>
          <Flex>
            <Text value={t.pfMediaRecente} size={11} style={{ color: 'var(--ink-500)' }} />
            <Text
              bold
              value={formatMs(anomaly.recentAvg)}
              size={14}
              style={{ color: borderColor }}
            />
          </Flex>
          <Flex>
            <Text value={t.pfBaseline} size={11} style={{ color: 'var(--ink-500)' }} />
            <Text bold value={formatMs(anomaly.baselineAvg)} size={14} />
          </Flex>
          <Flex>
            <Text value={t.pfRapporto} size={11} style={{ color: 'var(--ink-500)' }} />
            <Text bold value={`${anomaly.ratio}x`} size={14} />
          </Flex>
        </Flex>
        {anomaly.recentErrorRate > 0 && (
          <Text
            value={`${t.pfErroriRecenti}${anomaly.recentErrorRate}%${t.pfBaselineInfix}${anomaly.baselineErrorRate}%)`}
            size={11}
            className="perf-anomaly-card__error-note"
          />
        )}
      </div>
    </div>
  )
}

/** Incremento assoluto minimo (ms) della media recente sulla baseline per mostrare un alert. */
const MIN_AVG_INCREASE_MS = 1000

/**
 * Banner che mostra lo stato delle anomalie rilevate sulle unit di performance.
 * @returns {JSX.Element|null} Banner anomalie o stato OK
 */
export const AnomalyBanner = () => {
  const { data: anomalies = [], isSuccess } = useCQuery(['unitPerformanceAnomalies'])

  if (!isSuccess) return null

  // Alert solo se la media recente supera la baseline di almeno +1s in valore assoluto:
  // evita falsi positivi su tempi piccoli, dove un rapporto alto sono in realtà pochi ms.
  const alertAnomalies = anomalies.filter(
    (a) => a.status !== 'ok' && a.recentAvg - a.baselineAvg >= MIN_AVG_INCREASE_MS
  )

  if (!alertAnomalies.length) {
    return (
      <div className="nora-banner nora-banner--success">
        <div className="nora-banner__ico nora-banner__ico--success">
          <i className="pi pi-check-circle perf-icon-lg" />
        </div>
        <div className="nora-banner__text">
          <div className="nora-banner__title">{t.pfTuttiSistemiNormali}</div>
        </div>
      </div>
    )
  }

  return (
    <div className="nora-banner nora-banner--warning nora-banner--stacked">
      <div className="nora-banner__anomaly-header">
        <div className="nora-banner__ico nora-banner__ico--warning">
          <i className="pi pi-exclamation-triangle perf-icon-lg" />
        </div>
        <div className="nora-banner__text">
          <div className="nora-banner__title">
            {alertAnomalies.length} {t.pfAnomalieRilevate}
          </div>
        </div>
      </div>
      <div className="nora-banner__anomaly-cards">
        {alertAnomalies.map((a) => (
          <AnomalyCard key={a.unit} anomaly={a} />
        ))}
      </div>
    </div>
  )
}
